import React, { useEffect, useState } from "react";
import { Receipt, AlertTriangle, ArrowUpRight, TrendingUp } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useNavigate } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { fmtMoney } from "@/components/billing/billingMeta";

export default function BillingOverviewCard() {
  const [summary, setSummary] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    endpoints.me().then((u) => {
      endpoints.listConsultants().then((cs) => {
        const me = cs.find((c) => c.name === u.name);
        setAuthorized(!!me?.is_manager);
        if (me?.is_manager) endpoints.billingSummary().then(setSummary).catch(() => {});
      });
    });
  }, []);

  if (!authorized || !summary) return null;

  return (
    <div data-testid="billing-overview-card" className="glass-card rounded-3xl p-5 sm:p-6 h-full card-hover">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-8 w-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 flex items-center justify-center">
          <Receipt className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <h2 className="font-display font-bold text-[15px] tracking-tight">Client Billing</h2>
          <p className="text-xs text-slate-500">Cash flow at a glance</p>
        </div>
        <button
          data-testid="billing-view-all"
          onClick={() => navigate("/billing")}
          className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1"
        >
          Open <ArrowUpRight className="h-3 w-3" />
        </button>
      </div>

      <div className="rounded-2xl brand-gradient p-4 text-white shadow-md shadow-indigo-500/20 mb-3">
        <div className="text-[10px] font-semibold uppercase tracking-wider text-white/80">Total outstanding</div>
        <div data-testid="billing-outstanding-total" className="text-3xl font-extrabold font-display font-mono mt-1">
          {fmtMoney(summary.total_outstanding)}
        </div>
        <div className="text-[11px] text-white/85 mt-1">Across {summary.invoices_count} invoices</div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          data-testid="billing-overdue-link"
          onClick={() => navigate("/billing")}
          className="rounded-2xl border border-rose-200 dark:border-rose-500/25 bg-rose-50/60 dark:bg-rose-500/10 p-3 text-left hover:brightness-95 transition"
        >
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-rose-600">
            <AlertTriangle className="h-3 w-3" /> Overdue
          </div>
          <div className="text-lg font-bold font-mono text-rose-700 dark:text-rose-300 mt-1">
            {fmtMoney(summary.total_overdue)}
          </div>
          <Badge className="bg-rose-100 text-rose-700 border-rose-200 rounded-full text-[10px] mt-1">
            {summary.overdue_count} invoices
          </Badge>
        </button>
        <div className="rounded-2xl border border-emerald-200 dark:border-emerald-500/25 bg-emerald-50/60 dark:bg-emerald-500/10 p-3">
          <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-600">
            <TrendingUp className="h-3 w-3" /> Paid this month
          </div>
          <div className="text-lg font-bold font-mono text-emerald-700 dark:text-emerald-300 mt-1">
            {fmtMoney(summary.paid_this_month)}
          </div>
        </div>
      </div>

      {summary.clients && summary.clients.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Top outstanding clients</div>
          <div className="space-y-1.5">
            {summary.clients.filter((c) => c.outstanding > 0).slice(0, 3).map((c) => (
              <div key={c.client} className="flex items-center justify-between text-xs">
                <span className="font-medium truncate">{c.client}</span>
                <span className="font-mono font-bold text-rose-600">{fmtMoney(c.outstanding)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
